// apps/admin/src/components/ui/StatusBadge.tsx
//
// Phase 8.1 Wave 2 Task 06 — Color-coded status pill (status mapping).
//
// vg-binding: CONTEXT:decisions=P8.D-150,P8.D-152,P8.D-157
// vg-binding: CONTEXT:UI-SPEC=§4,§12.2
// vg-binding: CONTEXT:decisions (P999.11.D-14 — semantic-chip mechanical dark: pair)
// vg-binding: INTERFACE-STANDARDS:error-shape (N/A — style-only component, no error surface)
//
//  status mapping table — single canonical pill component
// used across Vendor/Merchant/Order/KYC/Subscription tables. Wave 3+ list
// pages pass a domain-typed status string; this component resolves it to
// the canonical color token.
//
// Why one component instead of N specialty badges:
//   • All status pills share the same visual shell (rounded-full, px-2,
//     text-xs, font-medium); only the color tone varies.
//   • Centralising the color map prevents drift when evolves
//     (e.g. adding a new "At-Risk" status — one place to update).
//   • Domain-specific helpers (VendorStatusBadge, MerchantStatusBadge,
//     KycStatusBadge) wrap this primitive with their narrowed status
//     union — kept lightweight to avoid an extra render layer.

import type { JSX, ReactNode } from 'react';

/** Canonical status tone — drives color tokens. */
export type StatusTone =
  | 'pending'
  | 'active'
  | 'suspended'
  | 'inactive'
  | 'rejected'
  | 'approved'
  | 'at_risk'
  | 'closed'
  | 'expired'
  | 'info'
  | 'neutral'
  | 'primary';

export interface StatusBadgeProps {
  /** Canonical tone — drives the color tokens. */
  tone: StatusTone;
  /** Display label rendered in the pill (e.g. "Active"). */
  label: ReactNode;
  /** Optional Lucide-style icon (ReactNode). */
  icon?: ReactNode;
  /** Optional Tailwind utility classes appended to the root span. */
  className?: string;
  /** Optional html title for hover tooltip. */
  title?: string;
  /** Deprecated — kept for prop compat; dots removed per DESIGN.md §7.5 (flat pills). */
  showDot?: boolean;
}

function toneClasses(tone: StatusTone): string {
  // Light: soft 100/800 semantic tint (unchanged).
  // Dark (P999.11.D-14 mechanical chip pair — keep hue, no structure change):
  //   bg-X-50|100  → +dark:bg-X-950/40  (muted tint, no pastel glare)
  //   text-X-600..800 → +dark:text-X-300 (readable on dark)
  // Neutral/inactive/closed chips use pw tokens (bg-pw-surface /
  //   text-pw-text-muted) which auto-flip via `.dark` — no dark: pair needed
  //   (P999.11.D-15 gray→token mapping preferred over a slate dark: pair).
  switch (tone) {
    case 'active':
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300';
    case 'pending':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
    case 'suspended':
    case 'rejected':
    case 'at_risk':
      return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300';
    case 'closed':
    case 'expired':
      return 'bg-pw-surface text-pw-text-muted line-through';
    case 'inactive':
      return 'bg-pw-surface text-pw-text-muted';
    case 'info':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300';
    case 'primary':
      return 'bg-[#FFF4EF] text-[#E8652D] dark:bg-[rgba(232,101,45,0.15)] dark:text-[#F07A45]';
    case 'neutral':
    default:
      return 'bg-pw-surface text-pw-text-muted';
  }
}

/**
 * `<StatusBadge>` — canonical status pill.
 *
 * Use directly when the parent already knows the desired tone, or via
 * the narrower domain wrappers (VendorStatusBadge, MerchantStatusBadge,
 * KycStatusBadge) which map a status string to the tone.
 */
export function StatusBadge(props: StatusBadgeProps): JSX.Element {
  const { tone, label, icon, className, title } = props;
  return (
    <span
      className={[
        // @printway/ui Badge tokens — `padding 3px 10px · gap 5px · radius 9999px
        // · 12px / 600 / 1.4`.
        'inline-flex items-center gap-[5px] rounded-full px-[10px] py-[3px]',
        'text-xs font-semibold leading-[1.4] whitespace-nowrap',
        toneClasses(tone),
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="status-badge"
      data-tone={tone}
      title={title}
    >
      {icon ? (
        <span aria-hidden="true" className="-ml-0.5">
          {icon}
        </span>
      ) : null}
      <span>{label}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Domain wrappers — narrow status unions for type-safety at call sites.
// ---------------------------------------------------------------------------

export type VendorStatus =
  | 'Pending'
  | 'Active'
  | 'Suspended'
  | 'Inactive'
  | 'AtRisk';

export interface VendorStatusBadgeProps {
  status: VendorStatus;
  className?: string;
}

function vendorTone(s: VendorStatus): StatusTone {
  switch (s) {
    case 'Pending':
      return 'pending';
    case 'Active':
      return 'active';
    case 'Suspended':
      return 'suspended';
    case 'Inactive':
      return 'inactive';
    case 'AtRisk':
      return 'at_risk';
  }
}

export function VendorStatusBadge(
  props: VendorStatusBadgeProps,
): JSX.Element {
  const label = props.status === 'AtRisk' ? 'At-Risk' : props.status;
  return (
    <StatusBadge
      tone={vendorTone(props.status)}
      label={label}
      className={props.className ?? ''}
    />
  );
}

export type MerchantStatus = 'Active' | 'Suspended' | 'Closed';

export interface MerchantStatusBadgeProps {
  status: MerchantStatus;
  className?: string;
}

function merchantTone(s: MerchantStatus): StatusTone {
  switch (s) {
    case 'Active':
      return 'active';
    case 'Suspended':
      return 'suspended';
    case 'Closed':
      return 'closed';
  }
}

export function MerchantStatusBadge(
  props: MerchantStatusBadgeProps,
): JSX.Element {
  return (
    <StatusBadge
      tone={merchantTone(props.status)}
      label={props.status}
      className={props.className ?? ''}
    />
  );
}

export type KycStatus = 'Pending' | 'Approved' | 'Rejected';

export interface KycStatusBadgeProps {
  status: KycStatus;
  className?: string;
}

function kycTone(s: KycStatus): StatusTone {
  switch (s) {
    case 'Pending':
      return 'pending';
    case 'Approved':
      return 'approved';
    case 'Rejected':
      return 'rejected';
  }
}

export function KycStatusBadge(props: KycStatusBadgeProps): JSX.Element {
  return (
    <StatusBadge
      tone={kycTone(props.status)}
      label={props.status}
      className={props.className ?? ''}
    />
  );
}

export default StatusBadge;
